import React from "react";

const SITE = "https://revivalfireministries.com";
const DEFAULT_IMAGE = `${SITE}/og-default.jpg`;

/**
 * SEO meta + JSON-LD using React 19's native head tag hoisting.
 * No external dependency needed — React 19 moves <title>/<meta>/<link>/<script>
 * elements rendered anywhere in the tree into <head> automatically.
 *
 * Usage:
 *   <SEO
 *     title="Prayer Ministry | RFMG"
 *     description="..."
 *     path="/prayer-ministry"
 *     image="https://..."
 *     noindex={false}
 *     jsonLd={[{...}, ...]}
 *   />
 */
const SEO = ({
  title,
  description,
  path = "/",
  image = DEFAULT_IMAGE,
  noindex = false,
  jsonLd = [],
  type = "website",
}) => {
  const url = path.startsWith("http") ? path : `${SITE}${path}`;
  const fullTitle = title.includes("Revival Fire") ? title : `${title} | Revival Fire Ministries Global`;
  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Revival Fire Ministries Global" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD — XSS-hardened: escape '<' so a malicious value can never close the <script> tag */}
      {Array.isArray(jsonLd) && jsonLd.map((schema, i) => (
        <script
          key={`ld-${i}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema).replace(/</g, "\\u003c"),
          }}
        />
      ))}
    </>
  );
};

/** Default Organization + WebSite schema applied site-wide on homepage. */
export const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Revival Fire Ministries Global",
  "alternateName": "RFMG",
  "url": SITE,
  "logo": `${SITE}/logo512.png`,
  "description": "Revival Fire Ministries Global is a prophetic and apostolic holiness revival ministry focused on prayer, deliverance, healing, biblical teaching, prophetic mentorship, marriage prayer, family covering, Christian guidance, discipleship, and spiritual growth through Jesus Christ.",
  "sameAs": [
    "https://www.facebook.com/profile.php?id=61570872987221",
    "https://www.youtube.com/@RevivalFireMinistries7",
    "https://www.tiktok.com/@revivalfireministries7",
    "https://www.instagram.com/revivalfireministries/",
  ],
  "areaServed": ["Dothan, Alabama", "Alabama", "United States", "Online"],
  "contactPoint": [{
    "@type": "ContactPoint",
    "contactType": "ministry inquiries",
    "email": "rfmgcontact@gmail.com",
    "availableLanguage": ["English"],
  }],
};

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Revival Fire Ministries Global",
  "alternateName": "RFMG",
  "url": SITE,
};

/** Breadcrumb helper — pass an array of {name, path}. */
export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((it, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "name": it.name,
    "item": `${SITE}${it.path}`,
  })),
});

/** FAQ schema helper — pass [{q, a}, ...]. */
export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map((f) => ({
    "@type": "Question",
    "name": f.q,
    "acceptedAnswer": { "@type": "Answer", "text": f.a },
  })),
});

export { SITE };
export default SEO;
